/**
 * CL-ACCESS-008 / 008A — Canonical drawing entitlement (pure).
 *
 * TWO GATES (both required for non-admin):
 * 1. Resolved active project collaborator + explicit permissions.canReviewDrawings === true
 * 2. Explicit View share for that collaborator + drawing
 *
 * View assignment contract:
 * - View = projectFileShares doc exists with visibility "shared" (default when omitted)
 *   and canView !== false. No separate View boolean; unchecking View deletes the doc.
 * - canComment grants C only when === true (missing/false deny)
 * - canUploadRevision grants R only when === true (missing/false deny)
 * - C/R never grant View
 *
 * Identity: only status "resolved" (exactly one active collaborator) may receive drawings.
 * auth_only / unresolved / ambiguous / removed / disabled → fail closed (zero drawings).
 *
 * Drawing entitlement is independent of accessScope / full_project.
 */

import { isDrawingReviewDomainFile } from "./clientDrawingReviewGroups";
import { isClientProjectDocument } from "./clientProjectDocuments";
import { isControlledProjectDocument } from "./controlledProjectDocuments";
import { normalizeShareEmail, shareDocIdForEmail, shareDocIdForUid } from "./projectFileShareIds";
import { isIndividualDrawing } from "./drawingDocumentRoleModel";

/** Exact admin-controlled Drawings & review module permission field. */
export const DRAWINGS_MODULE_PERMISSION_KEY = "canReviewDrawings";

export const DRAWING_ENTITLEMENT_COPY = {
  NONE_ASSIGNED: "No drawings have been assigned to you yet.",
  INDIVIDUAL_NONE: "No individual drawings have been shared with you yet.",
  IDENTITY_UNRESOLVED:
    "Your account is not fully linked for drawing access. Contact the project administrator to finish account linking.",
  IDENTITY_AMBIGUOUS:
    "Your account matches more than one collaborator identity. Contact the project administrator before drawings can be shown.",
  MODULE_DENIED:
    "Drawings & review is not enabled for your account on this project. Contact the project administrator if you need access.",
};

function safeStr(value) {
  return String(value ?? "").trim();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

function isActiveCollaboratorRow(row) {
  const st = lower(row?.status || "active");
  if (!st || st === "active") return true;
  return false;
}

/**
 * Whether a share row represents an explicit View/main assignment.
 */
export function shareGrantsDrawingView(share) {
  if (!share || typeof share !== "object") return false;
  const visibility = lower(share.visibility || "shared");
  if (visibility !== "shared") return false;
  if (share.canView === false) return false;
  if (share.shared === false) return false;
  const fileId = safeStr(share.fileId || share.id);
  return Boolean(fileId);
}

/** C only when explicitly true — missing/false deny. */
export function shareGrantsComment(share) {
  return shareGrantsDrawingView(share) && share?.canComment === true;
}

/** R only when explicitly true — missing/false deny. */
export function shareGrantsRevision(share) {
  return shareGrantsDrawingView(share) && share?.canUploadRevision === true;
}

/**
 * Explicit Drawings & review module permission (GATE 1).
 * Only true when permissions.canReviewDrawings === true (no missing→true).
 */
export function collaboratorHasDrawingsModulePermission(collaborator) {
  return collaborator?.permissions?.[DRAWINGS_MODULE_PERMISSION_KEY] === true;
}

/**
 * Pure viewer-identity resolver for drawing entitlement.
 *
 * Priority:
 * 1. exact collaborator authUid / uid === Auth uid among active rows → resolved (or ambiguous)
 * 2. exact Auth-token email among active rows → resolved only if unique
 * 3. otherwise unresolved — fail closed (no auth_only access)
 */
export function resolveDrawingViewerIdentity({
  authUid = "",
  authEmail = "",
  collaborators = [],
} = {}) {
  const uid = safeStr(authUid);
  const emailLower = normalizeShareEmail(authEmail);
  const rows = (Array.isArray(collaborators) ? collaborators : []).filter(isActiveCollaboratorRow);

  const base = {
    status: "unresolved",
    authUid: uid || null,
    authEmailLower: emailLower || null,
    collaboratorId: null,
    matchedCollaborator: null,
    shareMatchUids: [],
    shareMatchEmails: [],
    hasDrawingsModulePermission: false,
    message: DRAWING_ENTITLEMENT_COPY.IDENTITY_UNRESOLVED,
  };

  if (!uid && !emailLower) {
    return { ...base };
  }

  if (uid) {
    const byAuth = rows.filter((row) => {
      const rowAuth = safeStr(row?.authUid);
      const rowUid = safeStr(row?.uid);
      return rowAuth === uid || rowUid === uid;
    });
    if (byAuth.length === 1) {
      return buildResolvedIdentity(byAuth[0], uid, emailLower);
    }
    if (byAuth.length > 1) {
      return {
        ...base,
        status: "ambiguous",
        message: DRAWING_ENTITLEMENT_COPY.IDENTITY_AMBIGUOUS,
        shareMatchUids: [uid],
        shareMatchEmails: emailLower ? [emailLower] : [],
      };
    }
  }

  if (emailLower) {
    const byEmail = rows.filter((row) => normalizeShareEmail(row?.emailLower || row?.email) === emailLower);
    if (byEmail.length > 1) {
      return {
        ...base,
        status: "ambiguous",
        message: DRAWING_ENTITLEMENT_COPY.IDENTITY_AMBIGUOUS,
        shareMatchEmails: [emailLower],
        shareMatchUids: uid ? [uid] : [],
      };
    }
    if (byEmail.length === 1) {
      const row = byEmail[0];
      const rowAuth = safeStr(row?.authUid);
      const rowUid = safeStr(row?.uid);
      if (uid && rowAuth && rowAuth !== uid && rowUid && rowUid !== uid) {
        return {
          ...base,
          status: "unresolved",
          message: DRAWING_ENTITLEMENT_COPY.IDENTITY_UNRESOLVED,
          shareMatchEmails: [emailLower],
          shareMatchUids: [uid],
        };
      }
      return buildResolvedIdentity(row, uid, emailLower);
    }
  }

  // No auth_only path — authenticated non-collaborators fail closed.
  return { ...base };
}

function buildResolvedIdentity(row, authUid, authEmailLower) {
  const rowAuth = safeStr(row?.authUid);
  const rowUid = safeStr(row?.uid);
  const emailLower = normalizeShareEmail(row?.emailLower || row?.email) || authEmailLower || "";
  const shareMatchUids = [];
  if (authUid) shareMatchUids.push(authUid);
  if (rowAuth && !shareMatchUids.includes(rowAuth)) shareMatchUids.push(rowAuth);
  // Legacy CRM uid for share matching only when collaborator is resolved.
  if (rowUid && rowUid !== authUid && rowUid !== rowAuth && !shareMatchUids.includes(rowUid)) {
    shareMatchUids.push(rowUid);
  }
  const shareMatchEmails = emailLower ? [emailLower] : [];
  const hasModule = collaboratorHasDrawingsModulePermission(row);

  return {
    status: "resolved",
    authUid: authUid || rowAuth || null,
    authEmailLower: emailLower || null,
    collaboratorId: rowUid || rowAuth || null,
    matchedCollaborator: row,
    shareMatchUids,
    shareMatchEmails,
    hasDrawingsModulePermission: hasModule,
    message: hasModule ? null : DRAWING_ENTITLEMENT_COPY.MODULE_DENIED,
  };
}

/** Only fully resolved identities may pass GATE 1 identity half. */
export function drawingViewerIdentityIsUsable(identity) {
  return safeStr(identity?.status) === "resolved";
}

export function drawingViewerIdentityBlocksAccess(identity) {
  return !drawingViewerIdentityIsUsable(identity);
}

/**
 * GATE 1 complete: resolved active collaborator + explicit canReviewDrawings.
 * Uses collaborator.permissions.canReviewDrawings === true by default.
 * Optional canReviewDrawings=false denies (assignment/denorm explicit off).
 * Optional canReviewDrawings=true grants when assignment/denorm is explicitly true
 * even if the embedded collaborators[] row omitted the field (same admin field).
 */
export function passesDrawingsModuleGate(identity, { isAdmin = false, canReviewDrawings = null } = {}) {
  if (isAdmin) return true;
  if (!drawingViewerIdentityIsUsable(identity)) return false;
  if (canReviewDrawings === false) return false;
  if (identity?.hasDrawingsModulePermission === true) return true;
  if (canReviewDrawings === true) return true;
  return false;
}

export function shareMatchesDrawingViewer(share, identity) {
  if (!shareGrantsDrawingView(share) || !drawingViewerIdentityIsUsable(identity)) return false;
  const suid = safeStr(share.sharedWithUid);
  const sem = normalizeShareEmail(share.sharedWithEmail || share.sharedWithEmailLower);
  const docId = safeStr(share.id || share.__docId);
  const fileId = safeStr(share.fileId);

  const uids = Array.isArray(identity.shareMatchUids) ? identity.shareMatchUids.map(safeStr).filter(Boolean) : [];
  for (const uid of uids) {
    if (suid && suid === uid) return true;
    if (fileId && docId && docId === shareDocIdForUid(fileId, uid)) return true;
  }

  const emails = Array.isArray(identity.shareMatchEmails)
    ? identity.shareMatchEmails.map(normalizeShareEmail).filter(Boolean)
    : [];
  for (const em of emails) {
    if (sem && sem === em) {
      if (!suid) return true;
      if (uids.includes(suid)) return true;
      if (fileId && docId && docId === shareDocIdForEmail(fileId, em)) return true;
    }
  }
  return false;
}

/**
 * Canonical authorized drawing id set (GATE 1 + GATE 2).
 */
export function authorizedDrawingIds({
  viewerIdentity = null,
  isAdmin = false,
  canReviewDrawings = false,
  projectFileShares = [],
  projectFiles = null,
  projectId = "",
} = {}) {
  const pid = safeStr(projectId);

  if (isAdmin) {
    if (Array.isArray(projectFiles)) {
      return uniqueIds(
        projectFiles
          .filter((f) => isAuthorizedDrawingCandidateFile(f))
          .filter((f) => !pid || safeStr(f.projectId) === pid)
          .map((f) => safeStr(f.id || f.fileId))
      );
    }
    return [];
  }

  if (!passesDrawingsModuleGate(viewerIdentity, { isAdmin: false, canReviewDrawings })) {
    return [];
  }

  const drawingIdSet =
    Array.isArray(projectFiles) && projectFiles.length
      ? new Set(
          projectFiles
            .filter((f) => isAuthorizedDrawingCandidateFile(f))
            .map((f) => safeStr(f.id || f.fileId))
            .filter(Boolean)
        )
      : null;

  const ids = [];
  (Array.isArray(projectFileShares) ? projectFileShares : []).forEach((share) => {
    if (pid && safeStr(share?.projectId) && safeStr(share.projectId) !== pid) return;
    if (!shareMatchesDrawingViewer(share, viewerIdentity)) return;
    const fid = safeStr(share.fileId);
    if (!fid) return;
    // Exact fileId only. Filename/displayTitle/checksum/prior identity never transfer.
    if (drawingIdSet && !drawingIdSet.has(fid)) return;
    ids.push(fid);
  });
  return uniqueIds(ids);
}

export function isAuthorizedDrawingCandidateFile(file) {
  if (!file) return false;
  if (lower(file.status) === "deleted" || file.deleted === true) return false;
  if (isControlledProjectDocument(file)) return false;
  if (isClientProjectDocument(file)) return false;
  return isDrawingReviewDomainFile(file);
}

export function filterFilesByAuthorizedDrawingIds(files = [], authorizedIds = []) {
  const set = new Set((Array.isArray(authorizedIds) ? authorizedIds : []).map(safeStr).filter(Boolean));
  return (Array.isArray(files) ? files : []).filter((f) => set.has(safeStr(f?.id || f?.fileId)));
}

/** Individual drawing count for badges — overall package is never counted as another drawing. */
export function authorizedIndividualDrawingIds(args = {}) {
  const ids = authorizedDrawingIds(args);
  const files = Array.isArray(args.projectFiles) ? args.projectFiles : [];
  const byId = new Map();
  files.forEach((f) => {
    const id = safeStr(f?.id || f?.fileId);
    if (id) byId.set(id, f);
  });
  return ids.filter((id) => isIndividualDrawing(byId.get(id) || { id }));
}

export function countAuthorizedDrawings(args) {
  return authorizedIndividualDrawingIds(args).length;
}

export function drawingEntitlementEmptyMessage(viewerIdentity, { authorizedCount = 0, canReviewDrawings = null } = {}) {
  if (drawingViewerIdentityBlocksAccess(viewerIdentity)) {
    return safeStr(viewerIdentity?.message) || DRAWING_ENTITLEMENT_COPY.IDENTITY_UNRESOLVED;
  }
  if (!passesDrawingsModuleGate(viewerIdentity, { canReviewDrawings })) {
    return DRAWING_ENTITLEMENT_COPY.MODULE_DENIED;
  }
  if (authorizedCount <= 0) return DRAWING_ENTITLEMENT_COPY.NONE_ASSIGNED;
  return null;
}

export function canViewerAccessDrawingId(fileId, entitlementArgs) {
  const fid = safeStr(fileId);
  if (!fid) return false;
  if (entitlementArgs?.isAdmin) return true;
  return authorizedDrawingIds(entitlementArgs).includes(fid);
}

/** Dashboard / Overview singular-plural copy from canonical authorized count. */
export function formatAuthorizedDrawingCountLabel(count, { surface = "dashboard" } = {}) {
  const n = Math.max(0, Number(count) || 0);
  if (surface === "overview" || surface === "available") {
    if (n === 0) return "No drawings available";
    if (n === 1) return "1 drawing available";
    return `${n} drawings available`;
  }
  if (surface === "navAria") {
    if (n === 0) return "0 drawings available";
    if (n === 1) return "1 drawing available";
    return `${n} drawings available`;
  }
  // dashboard / project card
  if (n === 0) return "No drawings shared with you yet.";
  if (n === 1) return "1 drawing shared with you.";
  return `${n} drawings shared with you.`;
}

function uniqueIds(ids) {
  const out = [];
  const seen = new Set();
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    const v = safeStr(id);
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out;
}

/**
 * Admin collaborator count for one active file. Shares on deleted duplicates,
 * other fileIds, or C/R-only rows do not count.
 */
export function countActiveViewSharesForFileId({
  fileId = "",
  projectFileShares = [],
  projectFiles = [],
  projectId = "",
} = {}) {
  const fid = safeStr(fileId);
  if (!fid) return 0;
  const files = Array.isArray(projectFiles) ? projectFiles : [];
  if (files.length) {
    const file = files.find((f) => safeStr(f?.id || f?.fileId) === fid);
    if (!file || !isAuthorizedDrawingCandidateFile(file)) return 0;
  }
  const keys = [];
  (Array.isArray(projectFileShares) ? projectFileShares : []).forEach((share) => {
    if (safeStr(share?.fileId) !== fid) return;
    if (projectId && safeStr(share?.projectId) && safeStr(share.projectId) !== safeStr(projectId)) return;
    if (!shareGrantsDrawingView(share)) return;
    const uid = safeStr(share.sharedWithUid);
    const em = normalizeShareEmail(share.sharedWithEmail || share.sharedWithEmailLower);
    const key = uid || em;
    if (key) keys.push(key);
  });
  return uniqueIds(keys).length;
}

/**
 * Admin Overview / package card count for a collaborator row.
 */
export function countDrawingsInPackageForCollaborator({
  collaborator = null,
  collaborators = [],
  projectFileShares = [],
  projectFiles = [],
  projectId = "",
} = {}) {
  if (!collaborator || !isActiveCollaboratorRow(collaborator)) return 0;
  const roster = Array.isArray(collaborators) && collaborators.length ? collaborators : [collaborator];
  const identity = resolveDrawingViewerIdentity({
    authUid: safeStr(collaborator.authUid || collaborator.uid),
    authEmail: collaborator.emailLower || collaborator.email || "",
    collaborators: roster,
  });
  // Package inventory counts View assignments regardless of whether module is currently enabled,
  // so admin can prepare packages before enabling Drawings & review. Portal still enforces GATE 1.
  if (!drawingViewerIdentityIsUsable(identity)) return 0;
  const drawingIdSet =
    Array.isArray(projectFiles) && projectFiles.length
      ? new Set(
          projectFiles
            .filter((f) => isAuthorizedDrawingCandidateFile(f))
            .map((f) => safeStr(f.id || f.fileId))
            .filter(Boolean)
        )
      : null;
  const ids = [];
  (Array.isArray(projectFileShares) ? projectFileShares : []).forEach((share) => {
    if (projectId && safeStr(share?.projectId) && safeStr(share.projectId) !== safeStr(projectId)) return;
    if (!shareMatchesDrawingViewer(share, identity)) return;
    const fid = safeStr(share.fileId);
    if (!fid) return;
    if (drawingIdSet && !drawingIdSet.has(fid)) return;
    ids.push(fid);
  });
  return uniqueIds(ids).length;
}

/** Explicit booleans for new/updated share writes (never omit C/R). */
export function buildExplicitShareCapabilityFields({ canComment = false, canUploadRevision = false } = {}) {
  return {
    canComment: canComment === true,
    canUploadRevision: canUploadRevision === true,
  };
}
